import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../common/user";
import { overviewService } from "../../../services/admin/overview.service";
import { setGeneralProgressHide, setGeneralProgressShow } from "../../common/ui";

export type overviewSliceState = {
  overview: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  chart: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  chartOptions: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
};

const initialState: overviewSliceState = {
  overview: {
    data: null,
    error: null,
    isFetching: false,
  },
  chart: {
    data: null,
    error: null,
    isFetching: false,
  },
  chartOptions: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getOverviewData: any = createAsyncThunk(
  'admin/overview/getOverviewData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await overviewService.getOverview(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getChartData: any = createAsyncThunk(
  'admin/overview/getChartData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await overviewService.getChart(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getChartOptionsData: any = createAsyncThunk(
  'admin/overview/getChartOptions',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await overviewService.getChartOptions();
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const overviewSlice = createSlice({
  name: 'overview',
  initialState: initialState,
  reducers: {
    clearOverviewState(state) {
      state.overview.error = null;
      state.overview.isFetching = false;
      state.overview.data = null;
    },
    clearChartState(state) {
      state.chart.error = null;
      state.chart.isFetching = false;
      state.chart.data = null;
    },
    clearChartOptionsState(state) {
      state.chartOptions.error = null;
      state.chartOptions.isFetching = false;
      state.chartOptions.data = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<overviewSliceState>>) => {
    builder.addCase(getOverviewData.pending, (state) => {
      state.overview.isFetching = true;
      state.overview.error = null;
    });
    builder.addCase(getOverviewData.fulfilled, (state, action) => {
      state.overview.error = null;
      state.overview.isFetching = false;
      state.overview.data = action.payload;
      if (action.payload?.chart) {
        state.chart.data = action.payload.chart;
      }
    });
    builder.addCase(getOverviewData.rejected, (state, action) => {
      state.overview.isFetching = false;
      state.overview.error = action.payload;
      state.overview.data = null;
    });

    builder.addCase(getChartData.pending, (state) => {
      state.chart.isFetching = true;
      state.chart.error = null;
    });
    builder.addCase(getChartData.fulfilled, (state, action) => {
      state.chart.error = null;
      state.chart.isFetching = false;
      state.chart.data = action.payload;
    });
    builder.addCase(getChartData.rejected, (state, action) => {
      state.chart.isFetching = false;
      state.chart.error = action.payload;
      state.chart.data = null;
    });

    builder.addCase(getChartOptionsData.pending, (state) => {
      state.chartOptions.isFetching = true;
      state.chartOptions.error = null;
    });
    builder.addCase(getChartOptionsData.fulfilled, (state, action) => {
      state.chartOptions.error = null;
      state.chartOptions.isFetching = false;
      state.chartOptions.data = action.payload;
    });
    builder.addCase(getChartOptionsData.rejected, (state, action) => {
      state.chartOptions.isFetching = false;
      state.chartOptions.error = action.payload;
      state.chartOptions.data = null;
    });
  },
});

export const {
  clearOverviewState,
  clearChartState,
  clearChartOptionsState,
} = overviewSlice.actions;

export default overviewSlice.reducer;
